export const mcpPage = {
  seo: {
    title: "Akinda MCP Server — Shariah Compliance for AI Assistants",
    description:
      "Connect Claude, Cursor, and MCP clients to Akinda Shariah screening. Ask in plain language, get AAOIFI-aligned verdicts, ratios, and purification amounts.",
    keywords:
      "akinda mcp, shariah mcp server, halal stock mcp, claude mcp, cursor mcp, AAOIFI AI screening, islamic finance mcp",
    ogTitle: "Akinda MCP Server — Shariah Compliance for AI Assistants",
    ogDescription:
      "Put AAOIFI-aligned halal stock screening inside Claude, Cursor, and other AI assistants via MCP.",
  },
  hero: {
    label: "Akinda MCP Server",
    title: "Shariah compliance data, inside your AI assistant",
    lead: "Connect Claude, Cursor, or ChatGPT to Akinda and ask whether a stock is halal — with AAOIFI-aligned verdicts and ratios in the chat.",
    ctaPrimary: "Get your API key",
    ctaSecondary: "Read the docs",
    logosAria: "Supported AI assistants",
    worksWith: "Works with",
    logosNote: "Plus any MCP-compatible client via remote server URL.",
  },
  stats: {
    ariaLabel: "Platform stats",
    screenings: "screenings run",
    mau: "monthly active users",
    standard: "Standard No. 21",
    data: "financial data",
  },
  how: {
    label: "How it works",
    title: "Three steps from connect to verdict",
    lead: "No new dashboard to learn — your AI assistant becomes the interface.",
  },
  steps: {
    connect: {
      title: "Connect the server",
      description: "Add Akinda MCP once in Claude, Cursor, or your own client.",
    },
    ask: {
      title: "Ask in plain language",
      description: "Query any ticker or portfolio without leaving the chat.",
    },
    verdict: {
      title: "Get a structured verdict",
      description: "Receive pass/fail status with AAOIFI ratios and sources.",
    },
  },
  example: {
    label: "Live example",
    title: "Ask once. Get a structured verdict.",
  },
  chat: {
    ariaLabel: "Example chat with Akinda MCP",
    title: "Akinda MCP API: Top Halal Stocks",
    you: "You",
    userMessage: "Please fetch top 3 halal stocks from Akinda MCP.",
    assistantReply: "Certainly! Here are the top halal stocks from Akinda MCP.",
    toolTitle: "Akinda MCP API Request",
    success: "Success",
    resultTitle: "Top Halal Stocks",
    colRank: "#",
    colTicker: "Ticker",
    colCompany: "Company",
    colScore: "Score",
    colStatus: "Status",
    statusHalal: "Halal",
    footnote: "Sorted by Shariah Score (High to Low)",
    composerPlaceholder: "Reply to Claude…",
  },
  audience: {
    label: "Who it's for",
    title: "Built for people who invest — and build — with AI",
    investors: {
      title: "Individual investors",
      description:
        "Ask your AI assistant whether a stock fits Shariah guidelines before you buy.",
    },
    fintechs: {
      title: "Fintechs & Islamic banks",
      description:
        "Embed AAOIFI-aligned screening into advisory workflows your teams already use.",
    },
    developers: {
      title: "Developers building AI agents",
      description:
        "Give agents five read-only tools that return structured compliance data.",
    },
  },
  cta: {
    title: "Put AAOIFI screening inside the tools your team already uses",
    button: "Get your API key",
  },
  faq: {
    label: "FAQ",
    title: "Common questions",
    whatIsMcp: {
      q: "What is an MCP server?",
      a: "MCP (Model Context Protocol) lets AI assistants call external tools securely. Akinda’s MCP server exposes Shariah screening tools so Claude, Cursor, and similar clients can query compliance data in chat.",
    },
    aaoifi: {
      q: "Is Akinda's screening AAOIFI compliant?",
      a: "Yes. Screening follows AAOIFI Standard No. 21 for equity screening — business activity checks plus financial ratio thresholds for debt, liquidity, and non-compliant revenue.",
    },
    apiKey: {
      q: "Do I need an API key?",
      a: "Claude connects with Google sign-in on your Akinda subscription — no separate key to paste. API keys remain available for REST and custom clients from your B2B dashboard.",
    },
    assistants: {
      q: "Which AI assistants are supported?",
      a: "Claude (desktop and web connectors), Cursor, and any MCP-compatible client that can reach a remote HTTP MCP server.",
    },
    dataFreshness: {
      q: "How current is the data?",
      a: "Financial inputs and screening outputs refresh on an ongoing basis from reported filings and market data. Each report includes last-scanned / last-updated timestamps where available.",
    },
    freeTier: {
      q: "Is there a free tier?",
      a: "No. The MCP server requires a paid Akinda plan. Free accounts may connect in some clients, but tool calls remain blocked until you upgrade.",
    },
  },
} as const;
